import React, { useState } from "react";
import {
  Box, Container, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, CircularProgress, Tabs, Tab, Autocomplete, MenuItem
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ArrowBack as BackIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCases, useUpdateCase, useDeleteCase } from "../../hooks/useCases";
import { useTechnologies } from "../../hooks/useTechnologies";
import { supabase } from "../../lib/supabase";
import { adminColors, textFieldStyles, dialogStyles, tableStyles, buttonStyles, chipStyles, switchStyles } from "./adminStyles";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Switch } from "@mui/material";

const CASE_TYPES = [
  "Machine Learning Solution",
  "Data Strategy & Governance",
  "Deep Learning Architecture",
  "Predictive Analytics System",
  "Natural Language Processing",
  "Computer Vision Application",
  "Big Data Infrastructure",
  "AI Product Development",
  "Business Intelligence Dashboard",
  "Statistical Research Study",
  "Enterprise POC",
  "Market-Ready SaaS",
  "Custom Software Development",
  "SaaS Platform Architecture",
  "Mobile App Development",
  "Digital Transformation",
  "Strategic AI Consulting",
  "Cloud Native Migration",
  "UI/UX Product Design",
  "API & Microservices Design"
];

const AdminCases = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: cases, isLoading, refetch } = useCases({ isPublished: null, language: i18n.language });
  const { data: technologies } = useTechnologies({ language: i18n.language });
  const updateCase = useUpdateCase();
  const deleteCase = useDeleteCase();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    slug: "",
    client_name: "",
    case_label: "",
    cover_image_url: "",
    is_published: false,
    selectedTechs: [],
    translations: {
      fr: { title: "", excerpt: "", result: "", content: "" },
      en: { title: "", excerpt: "", result: "", content: "" },
    },
  });

  const handleOpenCreate = () => {
    setSelectedCase(null);
    setFormData({
      slug: "", client_name: "", case_label: "", cover_image_url: "", is_published: false,
      selectedTechs: [],
      translations: {
        fr: { title: "", excerpt: "", result: "", content: "" },
        en: { title: "", excerpt: "", result: "", content: "" },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (item) => {
    setSelectedCase(item);
    const frTrans = item.translations?.find((t) => t.language_code === "fr") || {};
    const enTrans = item.translations?.find((t) => t.language_code === "en") || {};
    
    const { data: relTechs } = await supabase.from("case_technologies").select("technology_id").eq("case_id", item.id);
    const initialTechs = technologies?.filter((t) => relTechs?.some((rt) => rt.technology_id === t.id)) || [];

    setFormData({
      slug: item.slug || "",
      client_name: item.client_name || "",
      case_label: item.case_label || "",
      cover_image_url: item.cover_image_url || "",
      is_published: item.is_published || false,
      selectedTechs: initialTechs,
      translations: {
        fr: { title: frTrans.title || "", excerpt: frTrans.excerpt || "", result: frTrans.result || "", content: frTrans.content || "" },
        en: { title: enTrans.title || "", excerpt: enTrans.excerpt || "", result: enTrans.result || "", content: enTrans.content || "" },
      },
    });
    setTabValue(0);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const caseData = {
        slug: formData.slug,
        client_name: formData.client_name,
        case_label: formData.case_label,
        cover_image_url: formData.cover_image_url || null,
        is_published: formData.is_published,
      };

      let caseId;
      if (selectedCase) {
        await updateCase.mutateAsync({ id: selectedCase.id, ...caseData });
        caseId = selectedCase.id;
        await supabase.from("case_technologies").delete().eq("case_id", caseId);
      } else {
        const newId = crypto.randomUUID();
        await supabase.from("cases").insert({ id: newId, ...caseData });
        caseId = newId;
      }

      for (const lang of ["fr", "en"]) {
        const transData = {
          case_id: caseId,
          language_code: lang,
          ...formData.translations[lang]
        };
        const { data: existing } = await supabase.from("case_translations").select("id").eq("case_id", caseId).eq("language_code", lang).maybeSingle();
        if (existing) {
          await supabase.from("case_translations").update(transData).eq("id", existing.id);
        } else {
          await supabase.from("case_translations").insert({ id: crypto.randomUUID(), ...transData });
        }
      }

      if (formData.selectedTechs.length > 0) {
        await supabase.from("case_technologies").insert(formData.selectedTechs.map(t => ({ case_id: caseId, technology_id: t.id })));
      }

      await refetch();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving case:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCase.mutateAsync(selectedCase.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting case:", err);
    }
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 20 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/admin")} sx={{ color: "white" }}><BackIcon /></IconButton>
            <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{t("admin.sections.cases")}</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={buttonStyles.primary}>{t("admin.actions.create")}</Button>
        </Box>

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t("admin.labels.client")}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t("admin.labels.title")} (FR)</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t("admin.labels.status")}</TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">{t("admin.labels.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cases?.map((item) => (
                <TableRow key={item.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>{item.client_name}</TableCell>
                  <TableCell sx={tableStyles.bodyCell}>{item.title}</TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip label={item.is_published ? t("admin.labels.published") : t("admin.labels.draft")} size="small" sx={item.is_published ? chipStyles.published : chipStyles.draft} />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton onClick={() => handleOpenEdit(item)} sx={buttonStyles.iconEdit}><EditIcon /></IconButton>
                    <IconButton onClick={() => { setSelectedCase(item); setDeleteDialogOpen(true); }} sx={buttonStyles.iconDelete}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: dialogStyles }}>
          <DialogTitle sx={{ color: "white", borderBottom: `1px solid ${adminColors.border.default}`, pb: 2 }}>
            {selectedCase ? t("admin.actions.edit") : t("admin.actions.create")} {t("admin.sections.cases")}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField label={t("admin.casesMeta.clientName")} fullWidth value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} sx={textFieldStyles} />
              <TextField label={t("admin.labels.slug")} fullWidth value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} sx={textFieldStyles} />
              
              <TextField
                select
                label={t("admin.casesMeta.caseLabel")}
                fullWidth
                value={formData.case_label}
                onChange={(e) => setFormData({ ...formData, case_label: e.target.value })}
                sx={textFieldStyles}
              >
                {CASE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              
              <Autocomplete multiple options={technologies || []} getOptionLabel={(o) => o.name} value={formData.selectedTechs} isOptionEqualToValue={(o, v) => o.id === v.id} onChange={(_, v) => setFormData({ ...formData, selectedTechs: v })} renderInput={(p) => <TextField {...p} label={t("admin.labels.technologies")} sx={textFieldStyles} />} />
              
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label={t("admin.translations.tabs.fr")} sx={{ color: 'white' }} />
                <Tab label={t("admin.translations.tabs.en")} sx={{ color: 'white' }} />
              </Tabs>
              {["fr", "en"].map((lang, idx) => tabValue === idx && (
                <Stack spacing={2} key={lang} sx={{ mt: 2 }}>
                  <TextField label={t("admin.labels.title")} fullWidth value={formData.translations[lang].title} onChange={(e) => setFormData({ ...formData, translations: { ...formData.translations, [lang]: { ...formData.translations[lang], title: e.target.value } } })} sx={textFieldStyles} />
                  <TextField label={t("admin.labels.excerpt")} fullWidth multiline rows={2} value={formData.translations[lang].excerpt} onChange={(e) => setFormData({ ...formData, translations: { ...formData.translations, [lang]: { ...formData.translations[lang], excerpt: e.target.value } } })} sx={textFieldStyles} />
                  <TextField label={t("admin.labels.content")} fullWidth multiline rows={4} value={formData.translations[lang].content} onChange={(e) => setFormData({ ...formData, translations: { ...formData.translations, [lang]: { ...formData.translations[lang], content: e.target.value } } })} sx={textFieldStyles} />
                  <TextField label={t("admin.labels.result")} fullWidth multiline rows={2} value={formData.translations[lang].result} onChange={(e) => setFormData({ ...formData, translations: { ...formData.translations, [lang]: { ...formData.translations[lang], result: e.target.value } } })} sx={textFieldStyles} />
                </Stack>
              ))}

              <TextField 
                label={t("admin.labels.coverImageUrl")} 
                fullWidth 
                value={formData.cover_image_url} 
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })} 
                sx={textFieldStyles} 
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    sx={switchStyles}
                  />
                }
                label={t("admin.labels.published")}
                sx={{ color: "white", mt: 2 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ color: "white" }}>{t("admin.actions.cancel")}</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={saving} sx={buttonStyles.primary}>{saving ? <CircularProgress size={24} /> : t("admin.actions.save")}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: dialogStyles }}>
          <DialogTitle sx={{ color: "white" }}>{t("admin.messages.confirmDelete")}</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "white" }}>{t("admin.actions.cancel")}</Button>
            <Button onClick={handleDelete} sx={{ color: "#EF4444" }}>{t("admin.actions.delete")}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminCases;
